/**
 * Episodes tab — upload + review + drag-to-reorder + start preprocess.
 *
 * Layout: responsive card grid that fills the available horizontal space.
 *   - 1 col on mobile, scales up to 5 cols on 2xl screens
 *   - Each card has: drag handle, EP # badge, filename + meta, status badge,
 *     Preview / Delete actions, and an expandable inline <video>
 *
 * Reorder: HTML5 drag-and-drop on the card body. Only enabled when all
 * episodes are pending/failed — once any segment depends on an episode_number
 * the renumber would break FKs.
 */
import { useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Upload,
  Loader2,
  FileVideo,
  CheckCircle2,
  XCircle,
  Trash2,
  Play,
  AlertTriangle,
  Sparkles,
  Plus,
  GripVertical,
} from 'lucide-react'

import { dramaApi, dramaQueryKeys } from '@/lib/api/drama'
import type { DramaEpisode, DramaLang } from '@/types/drama'
import { dramaT } from '@/components/drama/i18n'
import { AuthedImage } from '@/components/drama/AuthedImage'
import { AuthedVideo } from '@/components/drama/AuthedVideo'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { toast } from '@/components/ui/toaster'

interface Props {
  projectId: string
  existingEpisodes: DramaEpisode[]
  /** Source-content language string for ASR (project.language). */
  language?: string
  /** UI display language (global EN/中文 switch). */
  lang: DramaLang
}

const STATUS_VARIANT = {
  pending: 'secondary',
  processing: 'warning',
  ready: 'success',
  failed: 'destructive',
} as const

function formatSize(mb: number | null | undefined): string | null {
  if (mb == null) return null
  if (mb < 1) return `${(mb * 1024).toFixed(0)} KB`
  if (mb < 1024) return `${mb.toFixed(1)} MB`
  return `${(mb / 1024).toFixed(2)} GB`
}

export function EpisodeUploader({ projectId, existingEpisodes, language, lang }: Props) {
  const t = dramaT(lang)
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const [episodeNumber, setEpisodeNumber] = useState<number>(() => {
    const maxN = existingEpisodes.reduce((m, e) => Math.max(m, e.episode_number), 0)
    return maxN + 1
  })
  const [progress, setProgress] = useState<number | null>(null)
  const [draftFile, setDraftFile] = useState<File | null>(null)
  const [addFormOpen, setAddFormOpen] = useState(false)
  const [expanded, setExpanded] = useState<Set<number>>(new Set())

  // Drag-and-drop transient state (HTML5 native API, copying the pattern in
  // components/drama/TimelineTrack.tsx so we don't pull in a dnd library).
  const [dragFrom, setDragFrom] = useState<number | null>(null)
  const [dragOver, setDragOver] = useState<number | null>(null)

  const pendingCount = existingEpisodes.filter((e) => e.status === 'pending' || e.status === 'failed').length
  const processingCount = existingEpisodes.filter((e) => e.status === 'processing').length
  const readyCount = existingEpisodes.filter((e) => e.status === 'ready').length
  const reorderAllowed =
    existingEpisodes.length >= 2 &&
    existingEpisodes.every((e) => e.status === 'pending' || e.status === 'failed')

  const uploadMutation = useMutation({
    mutationFn: async () => {
      if (!draftFile) throw new Error('No file selected')
      return dramaApi.uploadEpisode(
        projectId,
        episodeNumber,
        draftFile,
        language,
        (loaded, total) => setProgress(Math.round((loaded / total) * 100)),
      )
    },
    onSuccess: (data) => {
      toast({
        title: t('toast.epUploaded'),
        description: t('toast.epUploadedDesc', data.size_mb, data.episode_number),
      })
      queryClient.invalidateQueries({ queryKey: dramaQueryKeys.detail(projectId) })
      setDraftFile(null)
      setProgress(null)
      if (inputRef.current) inputRef.current.value = ''
      setEpisodeNumber((n) => n + 1)
      setAddFormOpen(false)
    },
    onError: (e: any) => {
      toast({
        title: t('toast.uploadFailed'),
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      })
      setProgress(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (epNum: number) => dramaApi.deleteEpisode(projectId, epNum),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dramaQueryKeys.detail(projectId) })
    },
    onError: (e: any) => {
      toast({
        title: t('toast.deleteFailed'),
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      })
    },
  })

  const reorderMutation = useMutation({
    mutationFn: (newOrder: number[]) => dramaApi.reorderEpisodes(projectId, newOrder),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dramaQueryKeys.detail(projectId) })
    },
    onError: (e: any) => {
      toast({
        title: t('toast.reorderFailed'),
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      })
    },
  })

  const reprocessMutation = useMutation({
    mutationFn: (epNum: number) => dramaApi.reprocessEpisode(projectId, epNum),
    onSuccess: (data) => {
      toast({
        title: t('toast.epReset', data.episode_number),
        description: t('toast.epResetDesc'),
      })
      queryClient.invalidateQueries({ queryKey: dramaQueryKeys.detail(projectId) })
    },
    onError: (e: any) => {
      toast({
        title: t('toast.resetFailed'),
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      })
    },
  })

  const startMutation = useMutation({
    mutationFn: () => dramaApi.startPreprocess(projectId, language),
    onSuccess: (data) => {
      toast({
        title: t('toast.analysisStarted'),
        description: t('toast.analysisStartedDesc', data.queued_episodes.length),
      })
      queryClient.invalidateQueries({ queryKey: dramaQueryKeys.detail(projectId) })
      queryClient.invalidateQueries({ queryKey: dramaQueryKeys.progress(projectId) })
    },
    onError: (e: any) => {
      toast({
        title: t('toast.startFailed'),
        description: e?.response?.data?.detail || e.message,
        variant: 'destructive',
      })
    },
  })

  function togglePreview(ep: number) {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(ep)) next.delete(ep)
      else next.add(ep)
      return next
    })
  }

  function handleDrop(fromIdx: number, toIdx: number) {
    if (fromIdx === toIdx) return
    const sorted = [...existingEpisodes].sort((a, b) => a.episode_number - b.episode_number)
    const oldNums = sorted.map((e) => e.episode_number)
    const [moved] = oldNums.splice(fromIdx, 1)
    oldNums.splice(toIdx, 0, moved)
    reorderMutation.mutate(oldNums)
  }

  const canStart = pendingCount > 0 && processingCount === 0 && !startMutation.isPending
  const sortedEpisodes = [...existingEpisodes].sort((a, b) => a.episode_number - b.episode_number)

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 pb-3 border-b">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold">{t('tab.episodes')} ({existingEpisodes.length})</h2>
          <p className="text-xs text-muted-foreground mt-1">
            {existingEpisodes.length === 0 ? (
              t('episodes.empty')
            ) : pendingCount > 0 && processingCount === 0 ? (
              <>
                {t('episodes.pendingReady', pendingCount, readyCount)}
                {reorderAllowed && t('episodes.reorderHint')}
              </>
            ) : processingCount > 0 ? (
              t('episodes.processing', processingCount)
            ) : readyCount === existingEpisodes.length ? (
              t('episodes.allDone', readyCount)
            ) : null}
          </p>
        </div>
        {(pendingCount > 0 || processingCount > 0) && (
          <Button
            onClick={() => startMutation.mutate()}
            disabled={!canStart}
            size="lg"
            className="shrink-0"
          >
            {startMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('episodes.starting')}
              </>
            ) : processingCount > 0 ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t('episodes.analyzing')}
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                {t('episodes.startAnalysis', pendingCount)}
              </>
            )}
          </Button>
        )}
      </div>

      {/* Episode grid */}
      {sortedEpisodes.length === 0 ? (
        <div className="text-center py-12 border-2 border-dashed rounded-lg text-muted-foreground text-sm">
          <FileVideo className="w-8 h-8 mx-auto mb-2 text-muted-foreground/60" />
          {t('episodes.gridEmpty')}
        </div>
      ) : (
        <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
          {sortedEpisodes.map((ep, idx) => (
            <EpisodeCard
              key={ep.episode_number}
              ep={ep}
              index={idx}
              previewUrl={dramaApi.episodeFileUrl(projectId, ep.episode_number)}
              thumbnailUrl={dramaApi.episodeThumbnailUrl(projectId, ep.episode_number)}
              expanded={expanded.has(ep.episode_number)}
              reorderAllowed={reorderAllowed}
              reordering={reorderMutation.isPending}
              isDragFrom={dragFrom === idx}
              isDragOver={dragOver === idx && dragFrom !== null && dragFrom !== idx}
              onTogglePreview={() => togglePreview(ep.episode_number)}
              onDragStart={(e) => {
                if (!reorderAllowed) return
                setDragFrom(idx)
                e.dataTransfer.effectAllowed = 'move'
                e.dataTransfer.setData('text/plain', String(idx))
              }}
              onDragOver={(e) => {
                if (!reorderAllowed) return
                e.preventDefault()
                setDragOver(idx)
                e.dataTransfer.dropEffect = 'move'
              }}
              onDragLeave={() => {
                setDragOver((prev) => (prev === idx ? null : prev))
              }}
              onDrop={(e) => {
                e.preventDefault()
                const fromIdx = Number(e.dataTransfer.getData('text/plain'))
                setDragFrom(null)
                setDragOver(null)
                if (!Number.isNaN(fromIdx)) handleDrop(fromIdx, idx)
              }}
              onDragEnd={() => {
                setDragFrom(null)
                setDragOver(null)
              }}
              onDelete={() => {
                if (
                  confirm(
                    t('episodes.deleteConfirm', ep.episode_number, ep.original_filename || ''),
                  )
                ) {
                  deleteMutation.mutate(ep.episode_number)
                }
              }}
              deleting={deleteMutation.isPending && deleteMutation.variables === ep.episode_number}
              onReprocess={
                ep.status === 'ready'
                  ? () => {
                      if (
                        confirm(t('episodes.reanalyzeConfirm', ep.episode_number))
                      ) {
                        reprocessMutation.mutate(ep.episode_number)
                      }
                    }
                  : undefined
              }
              reprocessing={reprocessMutation.isPending && reprocessMutation.variables === ep.episode_number}
              t={t}
            />
          ))}
        </div>
      )}

      {/* Add-another section: collapsed by default */}
      <div className="border rounded-lg">
        {addFormOpen ? (
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <Upload className="w-4 h-4" /> {t('episodes.addNew')}
              </h3>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setAddFormOpen(false)}
                disabled={uploadMutation.isPending}
              >
                {t('episodes.cancel')}
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-[120px_1fr_auto] gap-3 items-end">
              <div>
                <Label htmlFor="ep_num">{t('episodes.epNum')}</Label>
                <Input
                  id="ep_num"
                  type="number"
                  min={1}
                  value={episodeNumber}
                  onChange={(e) => setEpisodeNumber(Math.max(1, parseInt(e.target.value || '1', 10)))}
                  disabled={uploadMutation.isPending}
                />
              </div>
              <div>
                <Label htmlFor="ep_file">{t('episodes.mp4File')}</Label>
                <Input
                  ref={inputRef}
                  id="ep_file"
                  type="file"
                  accept="video/mp4,video/*"
                  onChange={(e) => setDraftFile(e.target.files?.[0] ?? null)}
                  disabled={uploadMutation.isPending}
                />
              </div>
              <Button
                onClick={() => uploadMutation.mutate()}
                disabled={!draftFile || uploadMutation.isPending}
              >
                {uploadMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    {t('episodes.uploading')}
                  </>
                ) : (
                  t('episodes.upload')
                )}
              </Button>
            </div>
            {progress !== null && (
              <div className="mt-3">
                <Progress value={progress} className="h-2" />
                <div className="text-xs text-muted-foreground mt-1">
                  {t('episodes.uploadingPct', progress)}
                </div>
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAddFormOpen(true)}
            className="w-full p-3 flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground hover:bg-accent/30 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t('episodes.addNew')}
          </button>
        )}
      </div>
    </div>
  )
}

interface CardProps {
  ep: DramaEpisode
  index: number
  previewUrl: string
  thumbnailUrl: string
  expanded: boolean
  reorderAllowed: boolean
  reordering: boolean
  isDragFrom: boolean
  isDragOver: boolean
  onTogglePreview: () => void
  onDragStart: (e: React.DragEvent) => void
  onDragOver: (e: React.DragEvent) => void
  onDragLeave: () => void
  onDrop: (e: React.DragEvent) => void
  onDragEnd: () => void
  onDelete: () => void
  deleting: boolean
  onReprocess?: () => void
  reprocessing: boolean
  t: ReturnType<typeof dramaT>
}

function EpisodeCard({
  ep,
  index,
  previewUrl,
  thumbnailUrl,
  expanded,
  reorderAllowed,
  reordering,
  isDragFrom,
  isDragOver,
  onTogglePreview,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  onDelete,
  deleting,
  onReprocess,
  reprocessing,
  t,
}: CardProps) {
  const StatusIcon =
    ep.status === 'ready'
      ? CheckCircle2
      : ep.status === 'failed'
      ? XCircle
      : ep.status === 'processing'
      ? Loader2
      : AlertTriangle
  const canDelete = ep.status === 'pending' || ep.status === 'failed'
  const size = formatSize(ep.file_size_mb)
  const dim = ep.width && ep.height ? `${ep.width}×${ep.height}` : null
  const dur = ep.duration_seconds ? `${ep.duration_seconds.toFixed(1)}s` : null

  return (
    <div
      draggable={reorderAllowed && !reordering}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      data-testid={`episode-card-${ep.episode_number}`}
      className={[
        'rounded-md border bg-card overflow-hidden transition-all select-none relative group',
        reorderAllowed ? 'cursor-grab active:cursor-grabbing hover:border-spectrum-magenta/40 hover:shadow-md' : '',
        isDragFrom ? 'opacity-30 scale-95' : '',
        isDragOver ? 'ring-2 ring-spectrum-magenta ring-offset-2 shadow-lg' : '',
      ].join(' ')}
    >
      {/* Thumbnail banner (first frame extracted at upload time).
          Falls back to a film-icon placeholder if ffmpeg failed or the
          thumbnail hasn't been generated yet. */}
      <div className="relative aspect-video bg-muted overflow-hidden">
        {/* Auth'd fetch: the thumbnail endpoint is behind JWT, so a plain
            <img src> would 401. On failure AuthedImage renders nothing and the
            muted background + film icon below show through. */}
        <AuthedImage
          path={thumbnailUrl}
          alt={`EP ${ep.episode_number}`}
          className="w-full h-full object-cover"
        />
        <FileVideo
          className="absolute inset-0 m-auto w-10 h-10 text-muted-foreground/30 pointer-events-none"
          aria-hidden
        />
        {/* Overlay: drag handle + EP badge (top-left) + status badge (top-right) */}
        <div className="absolute inset-x-0 top-0 flex items-center gap-2 px-2 pt-2">
          {reorderAllowed && (
            <div
              className="flex items-center justify-center w-6 h-6 rounded bg-background/80 backdrop-blur-sm text-foreground/70 group-hover:text-spectrum-magenta group-hover:bg-background transition-colors"
              aria-label={t('episodes.dragReorder')}
              title={t('episodes.dragReorder')}
            >
              <GripVertical className="w-4 h-4" aria-hidden />
            </div>
          )}
          <Badge variant="secondary" className="font-mono bg-background/80 backdrop-blur-sm">
            EP {ep.episode_number}
          </Badge>
          <div className="flex-1" />
          <Badge
            variant={STATUS_VARIANT[ep.status] ?? 'secondary'}
            className="shrink-0 bg-background/90 backdrop-blur-sm"
          >
            <StatusIcon
              className={`w-3 h-3 mr-1 ${ep.status === 'processing' ? 'animate-spin' : ''}`}
            />
            {ep.status}
          </Badge>
        </div>
      </div>

      {/* Filename + meta */}
      <div className="px-3 pb-2">
        <div className="flex items-center gap-2">
          <FileVideo className="w-4 h-4 text-muted-foreground shrink-0" />
          <div
            className="text-sm font-medium truncate"
            title={ep.original_filename ?? undefined}
          >
            {ep.original_filename || `Episode ${ep.episode_number}`}
          </div>
        </div>
        <div className="mt-0.5 text-xs text-muted-foreground truncate pl-6">
          {[size, dim, dur].filter(Boolean).join(' · ') || '—'}
        </div>
      </div>

      {/* Action row */}
      <div className="flex items-center justify-between gap-1 px-2 pb-2 border-t pt-2">
        <Button
          size="sm"
          variant="ghost"
          onClick={onTogglePreview}
          className="text-xs"
        >
          <Play className="w-3.5 h-3.5 mr-1" />
          {expanded ? t('episodes.collapse') : t('episodes.preview')}
        </Button>
        {onReprocess && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onReprocess}
            disabled={reprocessing}
            className="text-xs"
            title={t('episodes.reanalyzeTitle')}
          >
            {reprocessing ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <>
                <Sparkles className="w-3.5 h-3.5 mr-1" />
                {t('episodes.reanalyze')}
              </>
            )}
          </Button>
        )}
        {canDelete && (
          <Button
            size="sm"
            variant="ghost"
            onClick={onDelete}
            disabled={deleting}
            className="text-destructive text-xs"
            aria-label={t('episodes.deleteAria')}
          >
            {deleting ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <>
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                {t('episodes.delete')}
              </>
            )}
          </Button>
        )}
      </div>

      {/* Index hint (for screen readers + small visual cue) */}
      <span className="sr-only">#{index + 1}</span>

      {expanded && (
        <div className="px-3 pb-3 bg-muted/30">
          <AuthedVideo
            path={previewUrl}
            stream
            controls
            preload="metadata"
            className="w-full rounded-md aspect-video"
          />
        </div>
      )}
    </div>
  )
}
